import React from "react";
import { Box, Typography, Avatar, Grid, ListItem, List, Tooltip, capitalize } from "@mui/material";
import RectangleIcon from "@mui/icons-material/Rectangle";
import moment from "moment";
import { useNavigate } from "react-router-dom";

interface APIRequestsTableProps {
    apis: any[];
    resourceStatuses: any[];
}

const APIRequestsTable: React.FC<APIRequestsTableProps> = ({ apis, resourceStatuses }) => {
    const [cardHoverIndex, setCardHoverIndex] = React.useState<number | null>(null);
    const navigate = useNavigate();
        
    const getStatusColor = (status: string) => {
        const lowerStatus = status?.toLowerCase();
        if (["active", "approved"].includes(lowerStatus)) return "#12B76A";
        if (lowerStatus === "rejected") return "#F04438";
        return "#FDB022";
    };

    const getStatusBgColor = (status: string) => {
        const lowerStatus = status?.toLowerCase();
        if (["active", "approved"].includes(lowerStatus)) return "#FFFFFF";
        if (lowerStatus === "rejected") return "#F0443810";
        return "#FDB02210";
    };

    return (
        <List sx={{ bgcolor: "#fafafa" }}>
            <ListItem sx={{ bgcolor: "#f2f2f2", borderRadius: "4px" }}>
                <Grid container direction="row" alignItems="center">
                    {/* Added fontFamily to headers for consistency */}
                    <Grid item xs={2.5}><Typography fontSize="12px" fontFamily="Gilroy-SemiBold">API BACKEND NAME</Typography></Grid>
                    <Grid item xs={1.5}><Typography fontSize="12px" fontFamily="Gilroy-SemiBold">APPLICATION</Typography></Grid>
                    <Grid item xs={1.5}><Typography fontSize="12px" fontFamily="Gilroy-SemiBold">SERVICE</Typography></Grid>
                    <Grid item xs={2}><Typography fontSize="12px" fontFamily="Gilroy-SemiBold">OWNER</Typography></Grid>
                    <Grid item xs={1.5}><Typography fontSize="12px" fontFamily="Gilroy-SemiBold">REQUESTED ON</Typography></Grid>
                    <Grid item xs={1.5}><Typography fontSize="12px" fontFamily="Gilroy-SemiBold">ACTIONED BY / REASON</Typography></Grid>
                    <Grid item xs={1.5}><Typography fontSize="12px" fontFamily="Gilroy-SemiBold">ACTIONED ON</Typography></Grid>
                </Grid>
            </ListItem>
            {apis.map((api: any, index: number) => (
                <ListItem
                    key={api.id}
                    sx={{
                        border: cardHoverIndex === index ? "1px solid #000" : "1px solid #00000000",
                        mt: 1,
                        cursor: "pointer",
                        bgcolor: getStatusBgColor(api.status),
                        borderRadius: "8px",
                    }}
                    onClick={() => navigate(`/apis/requests/details/${api.id}`)}
                    onMouseEnter={() => setCardHoverIndex(index)}
                    onMouseLeave={() => setCardHoverIndex(null)}
                >
                    <Grid container direction="row" alignItems="center">
                        <Grid item xs={2.5}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                 <RectangleIcon sx={{ color: api.status === "active" || api.status === "approved" ? "#12B76A" : api.status === "rejected" ? "#F04438" : "#FDB022", bgcolor: api.status === "active" || api.status === "approved" ? "#12B76A" : api.status === "rejected" ? "#F04438" : "#FDB022", width: "4px", marginRight: "8px" }} />
                                <Typography fontSize="14px" fontFamily="Gilroy-Medium">{api.apiName}</Typography>
                                {["active", "approved"].includes(api.status) && resourceStatuses?.length > 0 && ( 
                                    <Tooltip sx={{ whiteSpace: "pre-line" }} title={ <Box>Current Status<br />{resourceStatuses.filter(e => e.resourceId === api.id).map((e, i) => ( <React.Fragment key={i}>{capitalize(e.currentStatus)}<br /></React.Fragment> ))}</Box> }>
                                        <Avatar src="/assets/info.svg" alt="info" sx={{ height: 16, width: 16, ml: "4px" }} />
                                    </Tooltip>
                                )}
                            </Box>
                        </Grid>
                        {/* Added fontFamily to data cells for consistency */}
                        <Grid item xs={1.5}><Typography fontSize="14px" fontFamily="Gilroy-Regular">{api.applicationName || 'N/A'}</Typography></Grid>
                        <Grid item xs={1.5}><Typography fontSize="14px" fontFamily="Gilroy-Regular">{api.serviceName || 'N/A'}</Typography></Grid>
                        <Grid item xs={2}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Avatar src={api.apiOwnerAvatar} sx={{ mr: 1, height: 30, width: 30 }}>{api.apiOwnerName?.charAt(0).toUpperCase()}</Avatar>
                                <Typography fontSize="14px" fontFamily="Gilroy-Regular">{api.apiOwnerName}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={1.5}><Typography fontSize="14px" fontFamily="Gilroy-Regular">{api.createdAt ? moment(api.createdAt).format("DD/MM/YYYY | hh:mm A") : 'N/A'}</Typography></Grid>
                        <Grid item xs={1.5}>
                            {api.status !== "pending" && (
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    {api.apiApproverName && <Avatar 
                    src={api.apiApproverAvatar} // <-- USE the 'src' prop here
                    sx={{ mr: 1, height: 30, width: 30 }}
                >
                    {/* This is now the fallback */}
                    {api.apiApproverName.charAt(0).toUpperCase()}
                </Avatar>}
                                    {api.apiApproverName && <Typography fontSize="14px" fontFamily="Gilroy-Regular" mr={1}>{api.apiApproverName}</Typography>}
                                    {api.status === "rejected" && api.reasonForRejection && (
                                        <Tooltip title={`Reason: ${api.reasonForRejection}`}><img src="/assets/info.svg" alt="info" /></Tooltip>
                                    )}
                                </Box>
                            )}
                        </Grid>
                        <Grid item xs={1.5}>
                            {api.status !== "pending" && api.actionedAt && (
                                <Typography fontSize="14px" fontFamily="Gilroy-Regular">{moment(api.actionedAt).format("DD/MM/YYYY | hh:mm A")}</Typography>
                            )}
                        </Grid>
                    </Grid>
                </ListItem>
            ))}
        </List>
    );
};

export default APIRequestsTable;